import React, { useState } from "react";

/**
 * Järjestäjäksi nimeäminen adminin pelaajalistassa (docs/roolit.md).
 *
 * Rooli tulee voimaan vasta varmennetulla kirjautumisella: kutsulinkillä
 * tai sen jälkeen syntyneellä passkeyllä. Tapahtuman PIN-koodi antaa aina
 * vain pelaajan oikeudet, koska se on koko porukan yhteinen. Siksi
 * nimeäminen tarjoaa samalla kutsulinkin lähettämistä.
 */
export default function OrganizerRoleToggle({ isOrganizer, hasEmail, verifiedEver, onToggle, onSendInvite, lang }: {
  isOrganizer: boolean;
  hasEmail: boolean;
  /**
   * Onko pelaaja koskaan kirjautunut varmennetusti (passkey tallessa).
   *
   * Nappi kertoi vain sen, että rooli on myönnetty — ei sitä, onko se
   * voimassa. Ero tuli esiin 1.11.2026: pelaaja oli nimetty
   * järjestäjäksi mutta kirjautui tapahtuman PIN-koodilla, jolloin
   * `playerRole` palauttaa aina "player" eikä tulosten kirjaus
   * onnistunut. Ylläpitäjä näki napissa "✓ Järjestäjä" eikä voinut
   * tietää mistä kiikasti.
   */
  verifiedEver: boolean;
  onToggle: (next: boolean) => Promise<void>;
  onSendInvite: () => void;
  lang: "fi" | "en";
}) {
  const [busy, setBusy] = useState(false);
  const fi = lang !== "en";

  async function toggle() {
    if (busy) return;
    const next = !isOrganizer;
    setBusy(true);
    try {
      await onToggle(next);
      if (next && hasEmail && window.confirm(fi
        ? "Järjestäjän oikeudet tulevat voimaan, kun hän kirjautuu kutsulinkillä. Lähetetäänkö kutsulinkki nyt?"
        : "Organizer rights take effect once they sign in with an invite link. Send the invite link now?")) {
        onSendInvite();
      }
    } catch (err) {
      console.error("OrganizerRoleToggle", err);
      window.alert(fi
        ? "Roolia ei voitu tallentaa. Tarkista yhteys ja yritä uudelleen."
        : "Could not save the role. Check your connection and try again.");
    }
    setBusy(false);
  }

  return (
    <div style={{
      marginTop: 10, padding: "10px 12px", borderRadius: 10,
      background: isOrganizer ? "var(--sl-primary-a06)" : "var(--sl-soft)",
      border: `1px solid ${isOrganizer ? "var(--sl-primary-a20)" : "var(--sl-border)"}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--sl-text)" }}>
          📋 {fi ? "Järjestäjä" : "Organizer"}
        </div>
        <button onClick={toggle} disabled={busy} style={{
          border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700,
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          background: isOrganizer ? "var(--sl-primary)" : "var(--sl-card, #fff)",
          color: isOrganizer ? "#fff" : "var(--sl-primary)",
        }}>
          {isOrganizer ? (fi ? "✓ Järjestäjä" : "✓ Organizer") : (fi ? "Nimeä järjestäjäksi" : "Make organizer")}
        </button>
      </div>
      <div style={{ fontSize: 11, color: "var(--sl-muted)", marginTop: 6, lineHeight: 1.45 }}>
        {fi
          ? "Saa lähettää kutsut, arpoa ja lisätä otteluita, kirjata tulokset ja päättää illan. Oikeudet tulevat voimaan kutsulinkillä kirjautumisen jälkeen."
          : "Can send invites, generate and add matches, enter results and end the event. Rights take effect after signing in with an invite link."}
        {isOrganizer && !hasEmail && (
          <span style={{ color: "#b45309" }}>
            {fi ? " Lisää sähköposti, jotta kutsulinkin voi lähettää." : " Add an email so the invite link can be sent."}
          </span>
        )}
      </div>

      {/* Myönnetty rooli ei ole sama asia kuin voimassa oleva rooli. */}
      {isOrganizer && !verifiedEver && (
        <div style={{
          fontSize: 11, lineHeight: 1.45, marginTop: 8, padding: "7px 9px",
          borderRadius: 8, color: "#b45309",
          background: "rgba(180,83,9,0.08)", border: "1px solid rgba(180,83,9,0.25)",
        }}>
          {fi
            ? "⚠ Oikeudet eivät ole vielä voimassa: hän ei ole kirjautunut kutsulinkillä. Tapahtuman PIN-koodi antaa aina vain pelaajan oikeudet."
            : "⚠ Not in effect yet: they have not signed in with an invite link. The event PIN always gives player rights only."}
        </div>
      )}
    </div>
  );
}
